import type {
  LocalCase,
  LocalCaseBody,
  LocalSuite,
  Project,
  SourceRef,
  TestSuite,
  TestSuiteCase,
} from '@/lib/api/quality-types.js';

/**
 * The `workspace_id` the local store resolves a project from. A project linked to a registry
 * workspace is addressed by that workspace; an unlinked project is addressed by its own id, which
 * the service accepts as an adopted workspace.
 */
export function localStoreWorkspaceId(
  project: Pick<Project, 'id' | 'workspace_id'>,
): string {
  return project.workspace_id ?? project.id;
}

/** The own-tms launch identity of a stored suite. */
export function localSuiteSourceRef(
  projectId: string,
  suiteId: string,
): SourceRef {
  return {
    provider: 'own-tms',
    projectId,
    coordinates: { kind: 'suite', externalProjectId: projectId, suiteId },
  };
}

/** The launch descriptor for a stored suite, in the shape the Launch dialog reads for every source. */
export function localLaunchSuite(
  suite: LocalSuite,
  projectName: string,
): TestSuite {
  return {
    id: suite.id,
    source: 'own-tms',
    name: suite.name,
    createdOn: suite.created_at,
    projectId: suite.project_id,
    projectName,
    externalProjectId: suite.project_id,
    sourceRef:
      suite.source_ref ?? localSuiteSourceRef(suite.project_id, suite.id),
  };
}

/** A stored case in the read-only case view's shape. */
export function localCaseAsSuiteCase(testCase: LocalCase): TestSuiteCase {
  return {
    id: testCase.key,
    title: testCase.title,
    precondition: testCase.precondition,
    steps: testCase.test_steps,
    expectedResult: testCase.expected_result,
  };
}

export function emptyLocalCaseBody(): LocalCaseBody {
  return {
    title: '',
    precondition: '',
    test_steps: [{ content: '', expected: '' }],
    expected_result: '',
  };
}

/** A case can be saved once it has a title and every step says what to do. */
export function validLocalCaseBody(body: LocalCaseBody): boolean {
  return (
    body.title.trim().length > 0 &&
    body.test_steps.length > 0 &&
    body.test_steps.every((step) => Boolean(step.content?.trim()))
  );
}

/** The case ids in a new order after moving one case up or down by one place. */
export function movedCaseIds(
  cases: LocalCase[],
  caseId: string,
  direction: -1 | 1,
): string[] | null {
  const ids = cases.map((testCase) => testCase.id);
  const from = ids.indexOf(caseId);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  [ids[from], ids[to]] = [ids[to]!, ids[from]!];
  return ids;
}
